import { useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Sparkles, AlertTriangle, CheckCircle2, HelpCircle, Target,
  Loader2, RefreshCw, ShieldAlert, Info, FileQuestion, Stethoscope, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * V13.13 — AI Vault Summary Modal.
 * Button on each personal medical doc → SoulAI reads the doc (vision) and
 * returns a structured, calm-tone summary. Strict guardrails + disclaimer.
 *
 * Output states (from backend):
 *   ok | non_medical | unreadable | emergency_flagged | error
 */
export default function VaultSummaryDialog({ open, onClose, file, initialSummary }) {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(initialSummary || null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const runAnalysis = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(
        `/api/vault/files/${file.id}/summarize${force ? '?force_refresh=true' : ''}`
      );
      setSummary(res.data);
      if (res.data?.emergency?.flagged) {
        toast.warning('Worth discussing with your doctor soon');
      } else if (res.data?.status === 'ok') {
        toast.success('Analysis ready');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError({
        code: d?.code || 'UNKNOWN',
        message: d?.message || err.message || 'Analysis failed',
        cap: d?.cap,
        used: d?.used,
      });
      if (d?.code === 'DAILY_CAP_REACHED') {
        toast.error(d.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const askSoulAI = () => {
    // V14.2 — Open SoulAI chat IN-PAGE (was: navigate to /dashboard).
    // The widget is already mounted in LearnerLayout, so we just dispatch
    // a global event with a context-rich prefill. SoulAI's RAG side already
    // pulls the latest 5 vault summaries into _build_app_context, so the
    // chat will have the document content as system context — no need to
    // resend the summary in the prompt.
    const docName = file?.original_filename || 'document';
    const docType = summary?.document_type?.toLowerCase() || 'report';
    const prefill = `Can you explain my ${docType} (${docName}) in more detail and tell me what I should discuss with my doctor?`;
    window.dispatchEvent(new CustomEvent('open-soulai', {
      detail: { prefill, autoSubmit: false, source: 'vault_summary', file_id: file?.id },
    }));
    onClose?.();  // close the dialog so the chat is the focused surface
  };

  const handleClose = () => {
    setError(null);
    onClose?.();
  };

  if (!file) return null;

  const hasSummary = summary && summary.status !== 'error';
  const isEmergency = summary?.emergency?.flagged;
  const isNonMedical = summary?.status === 'non_medical';
  const isUnreadable = summary?.status === 'unreadable';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vault-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3 pr-6">
            <div className="p-2 rounded-full bg-gradient-to-br from-rose-100 to-purple-100 flex-shrink-0">
              <Sparkles className="h-5 w-5 text-rose-600" />
            </div>
            <div className="flex-1">
              <div className="text-base font-heading">SoulAI Document Summary</div>
              <div className="text-xs font-normal text-muted-foreground mt-0.5 truncate" title={file.original_filename}>
                {file.original_filename}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Initial state — not analyzed yet */}
        {!summary && !loading && !error && (
          <div className="space-y-4 py-2">
            <div className="rounded-xl bg-gradient-to-br from-rose-50 to-purple-50 p-5 border border-rose-100">
              <div className="flex items-start gap-3">
                <Stethoscope className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-rose-900">What you'll get</p>
                  <ul className="mt-2 space-y-1 text-rose-800 text-xs">
                    <li>• A layperson-friendly summary of what your document says</li>
                    <li>• What's good, what needs attention, what to focus on</li>
                    <li>• 3-5 questions you can ask your OBGYN at your next visit</li>
                    <li>• Gentle, calm tone — never panic-inducing</li>
                  </ul>
                </div>
              </div>
            </div>
            <DisclaimerNote />
            <Button
              className="w-full bg-gradient-to-r from-rose-500 to-purple-600 text-white"
              onClick={() => runAnalysis(false)}
              data-testid="vault-summary-analyze-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Analyze with SoulAI
            </Button>
            <p className="text-[11px] text-center text-muted-foreground">
              Analysis takes ~15-30 seconds. Results are cached — re-running uses your daily quota.
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="py-12 text-center space-y-3">
            <Loader2 className="h-10 w-10 mx-auto text-rose-500 animate-spin" />
            <p className="text-sm font-medium">SoulAI is reading your document…</p>
            <p className="text-xs text-muted-foreground">This usually takes 15-30 seconds</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="space-y-4 py-2">
            <Card className="border-amber-200 bg-amber-50 rounded-xl">
              <CardContent className="pt-4 pb-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">{errorTitle(error.code)}</p>
                  <p className="text-xs text-amber-700 mt-1">{error.message}</p>
                  {error.code === 'DAILY_CAP_REACHED' && error.cap && (
                    <p className="text-[11px] text-amber-700 mt-2">Used: {error.used} / {error.cap} today</p>
                  )}
                </div>
              </CardContent>
            </Card>
            {error.code !== 'DAILY_CAP_REACHED' && error.code !== 'UNSUPPORTED_TYPE' && (
              <Button onClick={() => runAnalysis(false)} variant="outline" className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />Try again
              </Button>
            )}
          </div>
        )}

        {/* Summary results */}
        {hasSummary && !loading && (
          <div className="space-y-4 py-2">
            {/* Header badges — V14.2: removed misleading "High Confidence"
                pill (heuristic, often gives false trust) AND "Cached — N hr ago"
                pill (showed stale timestamp from prior analysis after
                re-upload, see May 8 production triage). */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {summary.document_type || 'Document'}
              </Badge>
            </div>

            {/* Emergency banner */}
            {isEmergency && (
              <Card className="border-rose-300 bg-rose-50 rounded-xl">
                <CardContent className="pt-4 pb-4 flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-rose-900">Worth discussing with your doctor soon</p>
                    <p className="text-xs text-rose-800 mt-1">{summary.emergency?.reason}</p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        onClick={() => navigate('/soulai')}
                        data-testid="vault-summary-emergency-connect-btn"
                      >
                        <Stethoscope className="h-3.5 w-3.5 mr-1" />Connect with my doctor
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Non-medical / unreadable states */}
            {isNonMedical && (
              <Card className="border-slate-200 bg-slate-50 rounded-xl">
                <CardContent className="pt-4 pb-4 flex items-start gap-3">
                  <FileQuestion className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">This doesn't look like a medical document</p>
                    <p className="text-xs text-slate-600 mt-1">
                      SoulAI Vault only summarizes medical documents (reports, scans, prescriptions, etc.). Please upload a medical document to use this feature.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            {isUnreadable && (
              <Card className="border-amber-200 bg-amber-50 rounded-xl">
                <CardContent className="pt-4 pb-4 flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Couldn't read this clearly</p>
                    <p className="text-xs text-amber-700 mt-1">
                      The document may be too blurry or low-resolution. Try a clearer photo or PDF and upload again.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Structured summary (hidden for non-medical/unreadable) */}
            {!isNonMedical && !isUnreadable && (
              <div className="space-y-3">
                {summary.summary?.whats_good?.length > 0 && (
                  <SummarySection
                    icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    title="What's looking good"
                    items={summary.summary.whats_good}
                    accent="emerald"
                  />
                )}
                {summary.summary?.needs_attention?.length > 0 && (
                  <SummarySection
                    icon={<Info className="h-4 w-4 text-amber-600" />}
                    title="Worth discussing with your doctor"
                    items={summary.summary.needs_attention}
                    accent="amber"
                  />
                )}
                {summary.summary?.focus_areas?.length > 0 && (
                  <SummarySection
                    icon={<Target className="h-4 w-4 text-blue-600" />}
                    title="What to focus on"
                    items={summary.summary.focus_areas}
                    accent="blue"
                  />
                )}
                {summary.summary?.questions_for_obgyn?.length > 0 && (
                  <SummarySection
                    icon={<HelpCircle className="h-4 w-4 text-purple-600" />}
                    title="Questions to ask your OBGYN"
                    items={summary.summary.questions_for_obgyn}
                    accent="purple"
                  />
                )}
              </div>
            )}

            {/* Disclaimer */}
            <DisclaimerNote />

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              {!isNonMedical && !isUnreadable && (
                <Button variant="outline" className="flex-1" onClick={askSoulAI} data-testid="vault-summary-ask-soulai-btn">
                  <Sparkles className="h-4 w-4 mr-2" />Ask SoulAI follow-up
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => runAnalysis(true)}
                disabled={loading}
                data-testid="vault-summary-reanalyze-btn"
                className="sm:w-auto"
              >
                <RefreshCw className="h-4 w-4 mr-2" />Re-analyze
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummarySection({ icon, title, items, accent }) {
  const colors = {
    emerald: 'border-emerald-200 bg-emerald-50/40',
    amber: 'border-amber-200 bg-amber-50/40',
    blue: 'border-blue-200 bg-blue-50/40',
    purple: 'border-purple-200 bg-purple-50/40',
  }[accent] || '';
  return (
    <div className={`rounded-xl border ${colors} p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h4 className="text-sm font-medium">{title}</h4>
      </div>
      <ul className="space-y-1.5 text-[13px] leading-relaxed text-slate-700 ml-6">
        {items.map((it, i) => (
          <li key={i} className="list-disc">{it}</li>
        ))}
      </ul>
    </div>
  );
}

function DisclaimerNote() {
  return (
    <div className="rounded-lg bg-slate-100 px-3 py-2 flex items-start gap-2 text-[11px] text-slate-600">
      <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
      <p>
        <strong>AI-generated summary — not medical advice.</strong> This is for reference and self-education only. Always consult your OBGYN before taking any action.
      </p>
    </div>
  );
}

function errorTitle(code) {
  const map = {
    DAILY_CAP_REACHED: "You've reached today's analysis limit",
    UNSUPPORTED_TYPE: 'Unsupported file type',
    EMPTY_FILE: 'Empty file',
    UNREADABLE_PDF: 'PDF could not be read',
    UNREADABLE: 'Document could not be read',
    LLM_TIMEOUT: 'Analysis took too long',
    LLM_ERROR: 'SoulAI had a hiccup',
    BAD_MODEL_OUTPUT: 'Could not generate a clear summary',
    COOLDOWN: 'Slow down a moment',
    NOT_YOUR_FILE: 'Not your document',
  };
  return map[code] || 'Analysis couldn’t finish';
}

function timeAgo(iso) {
  if (!iso) return 'recently';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}
